import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { apiCallBegan } from "../actions/middlwares/api";
import { ucEndpoints } from "../config/config";
import { delayBeforeRedirect } from "../config/constants";
import { AppDispatch } from "../reducers";
import { getErrorMessageFromRequest } from "../utils/utils";
import { hide2FALoginField } from "./users";

export type TwoFactorAuthenticationSlice = {
  areRecoveryCodesLoading: boolean;
  confirmUserHasSavedRecoveryCodeFailure: boolean;
  confirmUserHasSavedRecoveryCodeFailureMessage: string | null;
  confirmUserHasSavedRecoveryCodeSuccess: boolean;
  disable2faByRecoveryCodeFailure: boolean;
  disable2faByRecoveryCodeMessage: string | null;
  disable2faByRecoveryCodeSuccess: boolean;
  disable2faFailure: boolean;
  disable2faMessage: string | null;
  disable2faSuccess: boolean;
  get2faStatusSuccess: boolean;
  getRecoveryCodesSuccess: boolean;
  is2faEnabled: boolean | null;
  isConfirmUserHasSavedRecoveryCodeLoading: boolean;
  isDisable2faByRecoveryCodeLoading: boolean;
  isDisable2faLoading: boolean;
  isGet2faStatusLoading: boolean;
  isSetup2faLoading: boolean;
  isSetup2faQrImageLoading: boolean;
  recoveryCodes: string | null;
  setup2faFailure: boolean;
  setup2faFailureMessage: string | null;
  setup2faQrImage: string | null;
  setup2faSuccess: boolean;
};

const initialState: TwoFactorAuthenticationSlice = {
  areRecoveryCodesLoading: false,
  confirmUserHasSavedRecoveryCodeFailure: false,
  confirmUserHasSavedRecoveryCodeFailureMessage: null,
  confirmUserHasSavedRecoveryCodeSuccess: false,
  disable2faByRecoveryCodeFailure: false,
  disable2faByRecoveryCodeMessage: null,
  disable2faByRecoveryCodeSuccess: false,
  disable2faFailure: false,
  disable2faMessage: null,
  disable2faSuccess: false,
  get2faStatusSuccess: false,
  getRecoveryCodesSuccess: false,
  is2faEnabled: null,
  isConfirmUserHasSavedRecoveryCodeLoading: false,
  isDisable2faByRecoveryCodeLoading: false,
  isDisable2faLoading: false,
  isGet2faStatusLoading: false,
  isSetup2faLoading: false,
  isSetup2faQrImageLoading: false,
  recoveryCodes: null,
  setup2faFailure: false,
  setup2faFailureMessage: null,
  setup2faQrImage: null,
  setup2faSuccess: false
};

const twoFactorAuthenticationSlice = createSlice({
  name: "twoFactorAuthentication",
  initialState,
  reducers: {
    startGetSetup2faQrImage(state) {
      state.isSetup2faQrImageLoading = true;
    },
    getSetup2faQrImageSuccess(state, action: PayloadAction<string>) {
      state.isSetup2faQrImageLoading = false;
      state.setup2faQrImage = action.payload;
    },
    getSetup2faQrImageFailure(state) {
      state.isSetup2faQrImageLoading = false;
      state.setup2faQrImage = null;
    },
    startSetup2fa(state) {
      state.isSetup2faLoading = true;
      state.setup2faSuccess = false;
      state.setup2faFailure = false;
      state.setup2faFailureMessage = null;
    },
    setup2faSuccess(state) {
      state.isSetup2faLoading = false;
      state.setup2faSuccess = true;
      state.setup2faFailure = false;
      state.setup2faFailureMessage = null;
    },
    setup2faFailure(state, action: PayloadAction<string>) {
      state.isSetup2faLoading = false;
      state.setup2faSuccess = false;
      state.setup2faFailure = true;
      state.setup2faFailureMessage = action.payload;
    },
    startGetRecoveryCodes(state) {
      state.areRecoveryCodesLoading = true;
    },
    getRecoveryCodesSuccess(state, action: PayloadAction<string>) {
      state.areRecoveryCodesLoading = false;
      state.recoveryCodes = action.payload;
    },
    getRecoveryCodesFailure(state) {
      state.areRecoveryCodesLoading = false;
      state.recoveryCodes = null;
    },
    startConfirmUserHasSavedRecoveryCode(state) {
      state.isConfirmUserHasSavedRecoveryCodeLoading = true;
      state.confirmUserHasSavedRecoveryCodeSuccess = false;
      state.confirmUserHasSavedRecoveryCodeFailure = false;
      state.confirmUserHasSavedRecoveryCodeFailureMessage = null;
    },
    confirmUserHasSavedRecoveryCodeFailure(state, action: PayloadAction<string>) {
      state.isConfirmUserHasSavedRecoveryCodeLoading = false;
      state.confirmUserHasSavedRecoveryCodeSuccess = false;
      state.confirmUserHasSavedRecoveryCodeFailure = true;
      state.confirmUserHasSavedRecoveryCodeFailureMessage = action.payload;
    },
    confirmUserHasSavedRecoveryCodeSuccess(state) {
      state.isConfirmUserHasSavedRecoveryCodeLoading = false;
      state.confirmUserHasSavedRecoveryCodeSuccess = true;
      state.confirmUserHasSavedRecoveryCodeFailure = false;
      state.confirmUserHasSavedRecoveryCodeFailureMessage = null;
    },
    startDisable2fa(state) {
      state.isDisable2faLoading = true;
      state.disable2faSuccess = false;
      state.disable2faFailure = false;
      state.disable2faMessage = null;
    },
    disable2faFailure(state, action: PayloadAction<string>) {
      state.isDisable2faLoading = false;
      state.disable2faSuccess = false;
      state.disable2faFailure = true;
      state.disable2faMessage = action.payload;
    },
    disable2faSuccess(state) {
      state.isDisable2faLoading = false;
      state.disable2faSuccess = true;
      state.disable2faFailure = false;
      state.disable2faMessage = null;
    },
    startDisable2faByRecoveryCode(state) {
      state.disable2faByRecoveryCodeFailure = false;
      state.disable2faByRecoveryCodeMessage = null;
      state.disable2faByRecoveryCodeSuccess = false;
      state.isDisable2faByRecoveryCodeLoading = true;
    },
    disable2faByRecoveryCodeFailure(state, action: PayloadAction<string>) {
      state.disable2faByRecoveryCodeFailure = true;
      state.disable2faByRecoveryCodeMessage = action.payload;
      state.disable2faByRecoveryCodeSuccess = false;
      state.isDisable2faByRecoveryCodeLoading = false;
    },
    disable2faByRecoveryCodeSuccess(state) {
      state.disable2faByRecoveryCodeFailure = false;
      state.disable2faByRecoveryCodeMessage = null;
      state.disable2faByRecoveryCodeSuccess = true;
      state.isDisable2faByRecoveryCodeLoading = false;
    },
    startGet2faStatus(state) {
      state.isGet2faStatusLoading = true;
      state.get2faStatusSuccess = false;
      state.is2faEnabled = null;
    },
    getGet2faStatusSuccess(state, action: PayloadAction<{ enabled: boolean }>) {
      state.isGet2faStatusLoading = false;
      state.get2faStatusSuccess = true;
      state.is2faEnabled = action.payload.enabled;
    },
    reset2faSetupState(state) {
      state.isGet2faStatusLoading = false;
      state.isSetup2faQrImageLoading = false;
      state.setup2faQrImage = null;
      state.isSetup2faLoading = false;
      state.setup2faSuccess = false;
      state.setup2faFailure = false;
      state.setup2faFailureMessage = null;
      state.areRecoveryCodesLoading = false;
      state.getRecoveryCodesSuccess = false;
      state.recoveryCodes = null;
      state.isConfirmUserHasSavedRecoveryCodeLoading = false;
      state.confirmUserHasSavedRecoveryCodeFailure = false;
      state.confirmUserHasSavedRecoveryCodeFailureMessage = null;
    },
    resetDisable2faByRecoveryCode(state) {
      state.disable2faByRecoveryCodeFailure = false;
      state.disable2faByRecoveryCodeMessage = null;
      state.disable2faByRecoveryCodeSuccess = false;
      state.isDisable2faByRecoveryCodeLoading = false;
    }
  }
});

export const getSetup2faQrImage = () => (dispatch: AppDispatch) => {
  dispatch(
    apiCallBegan({
      url: ucEndpoints.setup2fa,
      method: "get",
      responseType: "blob",
      onStart: startGetSetup2faQrImage,
      onSuccess: (response: Blob) => (dispatch: AppDispatch) =>
        dispatch(getSetup2faQrImageSuccess(URL.createObjectURL(response))),
      onFailure: getSetup2faQrImageFailure
    })
  );
};

export const setup2fa = (code: string) => (dispatch: AppDispatch) => {
  dispatch(
    apiCallBegan({
      url: ucEndpoints.setup2fa,
      method: "post",
      data: {
        code
      },
      onStart: startSetup2fa,
      onSuccess: (data: { recoveryCode: string | null }) => (dispatch: AppDispatch) => {
        if (data?.recoveryCode) {
          dispatch(getRecoveryCodesSuccess(data.recoveryCode));
        }
        dispatch(setup2faSuccess());
      },
      onFailure: ({ response }: AxiosError) => (dispatch: AppDispatch) => {
        if (response?.data?.message) {
          return dispatch(setup2faFailure(response.data.message));
        }
        dispatch(setup2faFailure("Failed to setup 2FA for the device"));
      }
    })
  );
};

export const getRecoveryCodes2fa = () => (dispatch: AppDispatch) => {
  dispatch(
    apiCallBegan({
      url: ucEndpoints.recoveryCode2fa,
      method: "get",
      onStart: startGetRecoveryCodes,
      onSuccess: getRecoveryCodesSuccess,
      onFailure: getRecoveryCodesFailure
    })
  );
};

export const confirmUserHasSavedRecoveryCode = () => (dispatch: AppDispatch) => {
  dispatch(
    apiCallBegan({
      url: ucEndpoints.setup2faHasSavedRecoveryCode,
      method: "post",
      onStart: startConfirmUserHasSavedRecoveryCode,
      onSuccess: confirmUserHasSavedRecoveryCodeSuccess,
      onSuccessMessage: "Two-factor authentication has been enabled for your account",
      onFailure: confirmUserHasSavedRecoveryCodeFailure
    })
  );
};

export const get2faStatus = () => (dispatch: AppDispatch) => {
  dispatch(
    apiCallBegan({
      url: ucEndpoints.status2fa,
      method: "get",
      onStart: startGet2faStatus,
      onSuccess: getGet2faStatusSuccess
    })
  );
};

export const disable2fa = () => (dispatch: AppDispatch) => {
  dispatch(
    apiCallBegan({
      url: ucEndpoints.disable2fa,
      method: "post",
      onStart: startDisable2fa,
      onSuccess: () => (dispatch: AppDispatch) => {
        dispatch(disable2faSuccess());
        dispatch(get2faStatus());
      },
      onSuccessMessage: "Two-factor authentication has been disabled",
      onFailure: disable2faFailure,
      onFailureMessage: "Failed to disable two-factor authentication"
    })
  );
};

/**
 * Disable 2FA by recovery code
 * Public endpoint: username and password must be included in the request as well
 *
 * @param {string} recoveryCode             - Recovery code
 * @param {string} username                 - Username
 * @param {string} password                 - Password
 */
export const disable2faByRecoveryCode = (
  recoveryCode: string,
  username: string,
  password: string
) => (dispatch: AppDispatch) => {
  dispatch(
    apiCallBegan({
      url: ucEndpoints.disable2faByRecoveryCode,
      method: "post",
      data: {
        username,
        password,
        recoveryCode
      },
      onStart: startDisable2faByRecoveryCode,
      onSuccess: () => (dispatch: AppDispatch) => {
        dispatch(disable2faByRecoveryCodeSuccess());
        return setTimeout(() => {
          dispatch(resetDisable2faByRecoveryCode());
          dispatch(hide2FALoginField());
        }, delayBeforeRedirect);
      },
      onFailure: (error: any) => (dispatch: AppDispatch) => {
        const errorText = getErrorMessageFromRequest(error);
        dispatch(disable2faByRecoveryCodeFailure(errorText));
        return setTimeout(() => {
          dispatch(resetDisable2faByRecoveryCode());
        }, delayBeforeRedirect);
      }
    })
  );
};

export const {
  startGetSetup2faQrImage,
  getSetup2faQrImageSuccess,
  getSetup2faQrImageFailure,
  startSetup2fa,
  setup2faSuccess,
  setup2faFailure,
  startGetRecoveryCodes,
  getRecoveryCodesSuccess,
  getRecoveryCodesFailure,
  startConfirmUserHasSavedRecoveryCode,
  confirmUserHasSavedRecoveryCodeSuccess,
  confirmUserHasSavedRecoveryCodeFailure,
  startGet2faStatus,
  getGet2faStatusSuccess,
  reset2faSetupState,
  startDisable2fa,
  disable2faSuccess,
  disable2faFailure,
  startDisable2faByRecoveryCode,
  disable2faByRecoveryCodeFailure,
  disable2faByRecoveryCodeSuccess,
  resetDisable2faByRecoveryCode
} = twoFactorAuthenticationSlice.actions;

export default twoFactorAuthenticationSlice.reducer;
